import humanizeString from 'humanize-string'
import React, { FC } from 'react'

import { ReviewCategory } from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { formatDateRange } from '@avantstay/format-date-range'

import { directLeadSources } from '../../constants'
import { DotRatings } from '../DotRatings'
import { RatingBar } from '../RatingBar'
// Import directly from the source modules to avoid circular re-export through the ReviewTimeline index
import { DetailedReview } from '../ReviewTimeline/components/DetailedReview/DetailedReview'
import { labelByReviewCategory } from '../ReviewTimeline/components/Review/Review.utils'
import { UserAvatar } from '../UserAvatar'
import * as S from './ReviewCard.styles'
import { ReviewCardProps } from './ReviewCard.types'
import { getOverallRating } from './ReviewCard.utils'

export const ReviewCard: FC<ReviewCardProps> = ({ onClickGuestName, review }) => {
  const { booking, bookingPeriod, categoryReviews, privateFeedback, response, source } = review

  const isSourceAvantStay = directLeadSources.includes(source)
  const overallRating = getOverallRating({ categoryReviews, isSourceAvantStay })
  const detailedRatings = categoryReviews.filter(
    reviewDetail =>
      reviewDetail.category !== ReviewCategory.overall &&
      reviewDetail.category !== ReviewCategory.nps,
  )
  const hasTagsOrCommentsForDetailedRatings = detailedRatings?.some(
    rating => rating.comment || rating.tags,
  )

  return (
    <S.ReviewCardContainer>
      {booking ? (
        <S.BookingDetails>
          <UserAvatar alt={booking.guestName} isActive size={34} />
          <div>
            <S.GuestNameLink
              onClick={onClickGuestName ? () => onClickGuestName(booking.guestId) : undefined}
            >
              {booking.guestName}
            </S.GuestNameLink>
            <S.BookingPeriodDates>
              {formatDateRange(bookingPeriod.start, bookingPeriod.end)}
            </S.BookingPeriodDates>
          </div>
        </S.BookingDetails>
      ) : null}
      <S.ReviewBreakdownContainer shouldDisplayAsColumn={hasTagsOrCommentsForDetailedRatings}>
        <S.OverallContainer>
          <S.ReviewRatingLabel>{isSourceAvantStay ? 'NPS' : 'Overall'}</S.ReviewRatingLabel>
          {overallRating != null && (
            <RatingBar isNPS={isSourceAvantStay} isOverall rating={overallRating.rating} />
          )}
        </S.OverallContainer>
        {hasTagsOrCommentsForDetailedRatings && detailedRatings?.length > 0 ? (
          <S.DetailedRatingsColumnsContainer>
            {detailedRatings?.map(reviewDetail => (
              <DetailedReview
                key={`detailedRatings-${review.id}-${reviewDetail.category}`}
                reviewDetail={reviewDetail}
              />
            ))}
          </S.DetailedRatingsColumnsContainer>
        ) : null}
        {!hasTagsOrCommentsForDetailedRatings && detailedRatings?.length > 0 ? (
          <S.DetailedRatingsRowContainer>
            {detailedRatings?.map(reviewDetail => (
              <DotRatings
                key={`detailedRatings-${review.id}-${reviewDetail.category}`}
                isSubRating
                category={
                  labelByReviewCategory[reviewDetail.category] ??
                  humanizeString(reviewDetail.category)
                }
                rating={reviewDetail.rating}
                shouldHideProgressBar
                shouldShowMaxRating
              />
            ))}
          </S.DetailedRatingsRowContainer>
        ) : null}
        <div>
          <S.ReviewCommentContainer>
            {overallRating?.comment && (
              <div>
                <S.ReviewCommentHeader>
                  {isSourceAvantStay ? 'Private NPS Review' : 'Public Review'}
                </S.ReviewCommentHeader>
                <S.ReviewComment>{overallRating?.comment}</S.ReviewComment>
              </div>
            )}
            {privateFeedback && (
              <div>
                <S.ReviewCommentHeader>Private Review</S.ReviewCommentHeader>
                <S.ReviewComment>{privateFeedback}</S.ReviewComment>
              </div>
            )}
            {response && (
              <div>
                <S.ReviewCommentHeader>Response</S.ReviewCommentHeader>
                <S.ReviewComment>{response}</S.ReviewComment>
              </div>
            )}
          </S.ReviewCommentContainer>
        </div>
      </S.ReviewBreakdownContainer>
    </S.ReviewCardContainer>
  )
}
